import { fireLightningBolt, BOLT_CONFIG } from "./fireLightningBolt";
import { Settings } from "../../settings";

/**
 * The coins fire their value INTO the collector.
 *
 * ── The Zeus bolt, unchanged, pointed the other way ──
 *
 * This is `fireLightningBolt` and nothing else: the same three-phase slingshot
 * the Zeus coins fire at their pots — charge and pull back at the source, shoot
 * along the gap accelerating, then shrink into the target. Its own doc says
 * "direction is irrelevant, pass any start/end", and that is the whole trick
 * here: the source is a COIN and the target is the MAGNET.
 *
 * Two things are asked of it, and only two:
 *
 *   withBalls: false   no energy ball at either end. A value going into a magnet
 *                      is a strike, not a launch.
 *   onImpact           the coin blanks when ITS bolt lands, not when the phase
 *                      ends — so what empties the coin is visibly the bolt that
 *                      left it.
 *
 * Everything else — the pullback, the bolt length maths, the per-frame
 * renormalisation that survives a resize, the dt cap, the pooled emitter
 * ownership, the sound — is the proven code, untouched.
 *
 * ── Why not a static bolt spanning the two cells ──
 *
 * That was tried first and it is wrong twice over: a stretched still frame has
 * no crackle, because the movement comes from the particle being a MovieClip on
 * loop, and a bolt that simply appears says nothing about which way the value
 * went. The strike carries the direction.
 */

/**
 * ── Adapting the Zeus bolt to a Mad Labs cell ──
 *
 * The emitter, the MovieClip art and the three-phase animation are Zeus's and
 * are not copied — they are literally the same objects. What cannot carry over
 * is the SCALE of the shot. Zeus fires a coin at a pot most of a screen away:
 * about an 800px gap, a 520px bolt, art authored at 74 x 459, so the jag is
 * drawn near its native size and every strand reads.
 *
 * A coin firing into its neighbouring collector is a fifth of that. Keep Zeus's
 * numbers and the same art is squashed to about a sixth of its authored length:
 * the zigzag collapses into a short fat dash, and the strands additively smear
 * into one solid smudge. That is the "close, but not it".
 *
 * So the shot is rebuilt for THIS distance. Four knobs, each with one job.
 */

/**
 * How much of the gap the bolt itself spans.
 *
 * `boltMultOverride: 0` used to hand this to the engine's own `dist * 0.5`
 * fallback — half the gap. Zeus does not run at half: its cell-derived minimum
 * (2 x symbolHeight) always exceeds the gap here, so it is pinned to the engine
 * CAP, `1 - MIN_TRAVEL_FRACTION` = 65%, and that longer bolt is a third more art
 * on screen to see the jag in.
 *
 * A multiplier of 3 reproduces that at any distance a 5-reel board can produce:
 * 3 x symbolHeight is past the cap for every realistic gap, so the cap decides
 * and the share is a constant 65%.
 */
const COLLECT_BOLT_LENGTH_MULT = 3;

/**
 * Thickness, as a fraction of the bolt's own length.
 *
 * Down from 0.2, because the bolt is now 30% longer and 0.2 of it is a slab.
 * Zeus's own measured ratio is 0.14; a little over it keeps the strike bright
 * at this size, since these particles blend ADDITIVELY and width is also how
 * much ink overlaps.
 *
 * Remember the particle's own taper multiplies this: each strand is born at
 * scaleX 3.0 and narrows to 0.6, so the flash at birth is three times this.
 */
const COLLECT_BOLT_WIDTH_FRACTION = 0.16;

/**
 * ── Travel time comes from the DISTANCE, not from a constant ──
 *
 * Zeus can use a flat 160ms because its gap barely varies. Here it varies by
 * four times over — a coin beside the collector against one in the far corner —
 * so one number cannot be right for both. A flat 210ms made the near ones drift
 * across at 0.38 px/ms against Zeus's 1.75: the bolt floated instead of struck.
 *
 * Speed is the constant instead, clamped at both ends: fast enough to read as a
 * strike, never so fast that a short hop is a single-frame twitch, and never so
 * slow that a long one crawls.
 *
 * ── SLOWED to sit beside the trail, 2026-09-10 ──
 *
 * Vladimir: "bolt is too fast, it feels like we can barely see it." It was
 * between 1.5x and 3.4x quicker than a trail over the same gap, and worst on
 * the SHORT hops, which is where the collect actually works — a one-cell hop
 * hit the 90ms floor and was gone in five frames.
 *
 *              gap    was            now           trail
 *              160  90ms 1.78px/ms  150ms 1.07   0.53
 *              320 124ms 2.57       172ms 1.86   1.07
 *              660 200ms 3.30       300ms 2.20   2.20
 *              900 200ms 4.50       300ms 3.00   3.00
 *
 * The ceiling is deliberately the TRAIL's flight time. Past ~660px the two are
 * then identical — a long bolt and a long trail cross the board in the same
 * 300ms — and below that the bolt stays the faster of the two, which is the
 * point: it strikes, the trail is thrown. If DEFAULT_DURATION ever moves in
 * LevelChargingAnimator, this ceiling moves with it.
 *
 * Still a SPEED with clamps, not a flat time — the reason for that is above and
 * has not changed. Only the three numbers have.
 */
const COLLECT_BOLT_SPEED_PX_PER_MS = 0.65;
const COLLECT_BOLT_MIN_MS = 150;
const COLLECT_BOLT_MAX_MS = 300;

/**
 * ── The look: density, turnover and crackle ──
 *
 * Overrides on the shared `Lightning` emitter, for this bolt only — Zeus keeps
 * what the artist authored (see FXManager.emitLightning).
 *
 * Every particle is a WHOLE bolt sprite stacked at the same place, each at its
 * own MovieClip frame. So the effect is not one drawn bolt: it is N of them
 * overlapping, and what reads as crackle is the frames disagreeing with each
 * other and being replaced.
 *
 * At Zeus's size ten strands is plenty, because each one is 520px of visible
 * jag. At a sixth of that length the same ten read as one smooth streak, so the
 * detail has to come from turnover instead of from size:
 *
 *   spawnCount      10 -> 14   more strands disagreeing at once
 *   spawnFrequency  .08-.12 -> .05-.08   a new generation ~1.6x more often
 *   duration        .1-.2 -> .08-.14   shorter life, so they turn over instead
 *                                      of piling up into a solid additive block
 *   animationSpeed  3-5 -> 5-8   the MovieClip cycling faster, which IS the
 *                                wiggle — the bolt is only on screen ~300ms
 *
 * Density is deliberately raised only a little. These blend additively and the
 * core already saturates at ten; the gain here is variety, not brightness.
 */
const COLLECT_BOLT_FX = {
  emitter: {
    spawnCountMin: 14,
    spawnCountMax: 14,
    spawnFrequencyMin: 0.05,
    spawnFrequencyMax: 0.08,
  },
  particle: {
    durationMin: 0.08,
    durationMax: 0.14,
  },
  component: {
    animationSpeedMin: 5.0,
    animationSpeedMax: 8.0,
  },
};


/**
 * Fire one bolt from every coin into the collector, all at once.
 *
 * Never blocks. No layer, no geometry or no particle bundle and it resolves
 * immediately — the collection matters more than the picture of it, and the
 * caller still empties the coins.
 *
 * @param   {Object} p
 * @param   {PIXI.Container} p.layer  Where the bolts are drawn — the same trail
 *          layer the multiplier volley uses, so they sit above the board.
 * @param   {{x:number,y:number}} p.to  The collector's centre, in `layer` space.
 * @param   {Array<{point:{x:number,y:number}, onImpact?:Function}>} p.coins
 *          One entry per coin being taken: where it is, and what happens when
 *          the bolt it fired arrives.
 * @param   {Function} [p.getDimensions] () => ({symbolWidth, symbolHeight}),
 *          read EVERY FRAME by the bolt so a resize mid-flight is survivable.
 * @param   {number} [p.duration]
 * @returns {Promise<void>} resolves when every bolt has FINISHED — not when it
 *          arrived. The two are 120ms apart: `onImpact` fires at the START of the
 *          bolt's impact phase, and the strike shrinking into the target runs for
 *          IMPACT_MS after it. The magnet's total hangs off this promise, so the
 *          number lands with the last of the lightning rather than under it.
 */
export const fireCollectBolts = ({
  layer,
  to,
  coins = [],
  getDimensions = null,
  duration = null,
} = {}) => {
  if (!layer || layer.destroyed || !to || !coins.length) {
    return Promise.resolve();
  }

  // Owned per volley. Every bolt splices itself out on completion — including
  // its abort paths — so this is empty again by the time the promise settles.
  const activeBolts = [];

  const dims =
    getDimensions ||
    (() => ({
      symbolWidth: Settings.SYMBOL_WIDTH,
      symbolHeight: Settings.SYMBOL_HEIGHT,
    }));

  const flights = [];
  for (const coin of coins) {
    if (!coin?.point) continue;

    // ── This bolt's own travel time ──
    //
    // The engine measures the gap per frame, but it needs the duration up
    // front, so it is measured here as well. Only the part the bolt actually
    // MOVES counts: the rest of the gap is covered by the bolt's own body, and
    // pricing that as travel is what made the near ones look becalmed.
    const gap = Math.hypot(to.x - coin.point.x, to.y - coin.point.y);
    const travelPx = gap * BOLT_CONFIG.MIN_TRAVEL_FRACTION;
    const boltMs =
      duration ??
      Math.min(
        COLLECT_BOLT_MAX_MS,
        Math.max(COLLECT_BOLT_MIN_MS, travelPx / COLLECT_BOLT_SPEED_PX_PER_MS),
      );

    flights.push(
      fireLightningBolt(layer, coin.point, to, {
        getDimensions: dims,
        duration: boltMs,
        activeBolts,
        withBalls: false,
        onImpact: () => coin.onImpact?.(),
        // Shape from the gap, not from the cell — see the constants above.
        boltMultOverride: COLLECT_BOLT_LENGTH_MULT,
        widthFraction: COLLECT_BOLT_WIDTH_FRACTION,
        fxOverrides: COLLECT_BOLT_FX,
      }),
    );
  }

  if (!flights.length) return Promise.resolve();
  return Promise.all(flights).then(() => undefined);
};

export default fireCollectBolts;
